import * as React from "react";
import { useQuery } from "@apollo/client";
import Image from "next/image";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  GetWishListItemsQuery,
  GetWishListItemsQueryVariables,
} from "@src/@types/api.d";
import { GetWishListItems } from "@src/graphql/queries";
import defaultImg from "../../../../public/images/bw-flower.jpeg";

type RegistryProps = {
  eventId: string;
};

const isUsableImage = (image?: string | null) =>
  !!image && image !== "not set" && /^https?:\/\//i.test(image);

const Registry: React.FC<RegistryProps> = ({ eventId }) => {
  const { data, loading } = useQuery<
    GetWishListItemsQuery,
    GetWishListItemsQueryVariables
  >(GetWishListItems, {
    variables: { eventId, page: 1, pageSize: 60 },
    skip: !eventId,
  });

  const wishlistItems = (data?.getWishListItems ?? []).filter(Boolean);

  return (
    <section className="relative w-full overflow-hidden bg-[#FAF7F5] px-6 py-10 md:px-10 md:py-16">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />

      <div className="relative mx-auto max-w-5xl">
        <div className="mb-12 text-center md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            If you would like to bless us
          </span>
          <h2 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            Gift <em className="font-light italic text-[#228B22]">Registry</em>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        <article className="border border-[#228B22]/18 bg-white/78 px-5 py-10 shadow-[0_24px_70px_rgba(28,24,32,0.08)] md:px-12 md:py-14">
          <p className="mx-auto max-w-3xl text-center font-corGaramond text-xl font-light italic leading-[1.9] text-[#401d15]/62 md:text-2xl">
            Your presence at our celebration means the world to us. For those
            who would like to honour us with a gift, we would be grateful for
            something from our wishlist as we begin this new journey together.
          </p>

          <div className="mt-10">
            {loading ? (
              <p className="text-center font-jost text-[0.62rem] font-medium uppercase tracking-[0.25em] text-[#228B22]">
                Loading registry
              </p>
            ) : wishlistItems.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {wishlistItems.map((item) => {
                  if (!item) return null;

                  const amount =
                    item.amount != null
                      ? `${
                          getSymbolFromCurrency(item.currency ?? "") ?? ""
                        }${Number(item.amount).toLocaleString()}`
                      : "Amount not set";
                  const imageSrc = isUsableImage(item.image)
                    ? (item.image as string)
                    : defaultImg;

                  return (
                    <div
                      key={item.id}
                      className="flex gap-4 border border-[#228B22]/16 bg-[#FAF7F5]/80 p-4"
                    >
                      <div className="relative h-24 w-24 shrink-0 overflow-hidden bg-[#E9E3DF]">
                        <Image
                          src={imageSrc}
                          alt={item.name ?? "Wishlist item"}
                          layout="fill"
                          objectFit="cover"
                        />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col justify-center">
                        <h3 className="font-corGaramond text-2xl font-normal leading-tight text-[#401d15]">
                          {item.name ?? "Wishlist item"}
                        </h3>
                        <p className="mt-1 font-jost text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#228B22]">
                          {amount}
                          {item.qty ? ` · Qty ${item.qty}` : ""}
                        </p>
                        {item.paid && (
                          <span className="mt-2 font-jost text-[0.52rem] font-medium uppercase tracking-[0.22em] text-[#401d15]/45">
                            Fulfilled
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center font-corGaramond text-xl font-light italic leading-[1.75] text-[#401d15]/60">
                The registry items will appear here soon.
              </p>
            )}
          </div>

          <p className="mt-10 text-center font-corGaramond text-lg font-light italic text-[#401d15]/52">
            Thank you for your love and generosity.
          </p>
        </article>
      </div>
    </section>
  );
};

export default Registry;
